import { useEffect, useMemo, useState } from "react";
import { useSession, useUser } from "@clerk/react";
import { Button, Chip, InputGroup, ScrollShadow } from "@heroui/react";
import { Check, Plus, Search, Users, X } from "lucide-react";

import { getMyConnections as getUserMembersApi } from "@/api/users";
import MemberAvatar from "@/components/members/member-avatar";
import CreateMemberDrawer from "@/components/drawers/create-member-drawer";
import DesktopCreateMemberDrawer from "../../drawers/desktop-create-connection-drawer";

const ParticipantsStep = ({ formData, updateFormData }) => {
  const { session } = useSession();
  const { user } = useUser();

  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const participants = formData.participants ?? [];

  useEffect(() => {
    if (!session) return;
    getMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  async function getMembers() {
    setLoading(true);
    try {
      const token = await session.getToken();
      const _resp = await getUserMembersApi(token);
      if (_resp.data.Result === "OK") {
        const records = (_resp.data.Records || []).filter((rec) => String(rec.id) !== String(user?.id));
        setAllUsers(records);
      } else {
        setAllUsers([]);
      }
    } catch {
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }

  // Drives selected/unselected rendering — derived from formData, not local state.
  const selectedIds = useMemo(() => new Set(participants.map((p) => String(p.id))), [participants]);

  const filteredUsers = useMemo(() => {
    if (loading) return [];
    if (!search.trim()) return allUsers;
    const q = search.toLowerCase();
    return allUsers.filter((u) => u.fullname?.toLowerCase().includes(q) || u.email_address?.toLowerCase().includes(q));
  }, [allUsers, search, loading]);

  const toggleMember = (member) => {
    const id = String(member.id);
    const isSelected = selectedIds.has(id);
    const next = isSelected ? participants.filter((p) => String(p.id) !== id) : [...participants, member];
    updateFormData({ participants: next });
  };

  function onCreateMemberDrawerResp(_cmd) {
    if (_cmd === "close") {
      setShowCreate(false);
      getMembers();
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* ── Picker ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col grow gap-4">
        <div className="flex items-center gap-2">
          <InputGroup
            fullWidth
            className="h-10 border border-accent/20 bg-accent/8 dark:border-[#FFFFFF26] dark:bg-[#FFFFFF21]"
          >
            <InputGroup.Prefix>
              <Search size={15} className="text-muted" />
            </InputGroup.Prefix>
            <InputGroup.Input
              placeholder="Search people by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="dark:border-[#FFFFFF26]"
            />
            {search && (
              <InputGroup.Suffix>
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="flex h-6 w-6 items-center justify-center rounded-full text-muted transition-colors hover:bg-default hover:text-foreground"
                >
                  <X size={13} />
                </button>
              </InputGroup.Suffix>
            )}
          </InputGroup>

          <Button
            type="button"
            variant="outline"
            onClick={() => setShowCreate(true)}
            className="h-10 shrink-0 border-accent/30 font-semibold text-accent dark:border-[#FFFFFF26] dark:text-white"
          >
            <Plus size={15} />
            New contact
          </Button>
        </div>

        {participants.length > 0 && (
          <ScrollShadow hideScrollBar className="w-full flex py-0.5" orientation="horizontal">
            {participants.map((member) => (
              <button type="button" key={member.id} onClick={() => toggleMember(member)} className="shrink-0">
                <Chip className="me-1 bg-accent/15 font-meduim border-accent border-1 text-accent dark:bg-[#FFFFFF2B] dark:text-white dark:border-white dark:border-1 flex items-center gap-1">
                  <span className="capitalize">{member.fullname?.split(" ")[0]}</span>
                  <Check size={12} />
                </Chip>
              </button>
            ))}
          </ScrollShadow>
        )}

        {loading && <p className="py-10 text-center text-sm text-muted">Loading people...</p>}

        {!loading && filteredUsers.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-separator py-12">
            <Users size={22} strokeWidth={1.5} className="text-muted" />
            <p className="text-sm text-muted">{search ? "No one matches that search." : "Your network is empty."}</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowCreate(true)}
              className="mt-1 border-accent/30 text-accent dark:border-[#FFFFFF26] dark:text-white"
            >
              <Plus size={14} />
              Add a contact
            </Button>
          </div>
        )}

        {!loading && filteredUsers.length > 0 && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-1 lg:grid-cols-2">
            {filteredUsers.map((person) => {
              const isSelected = selectedIds.has(String(person.id));
              return (
                <button
                  type="button"
                  key={person.id}
                  onClick={() => toggleMember(person)}
                  aria-pressed={isSelected}
                  className={[
                    "flex items-center gap-2.5 rounded-2xl border px-3 py-2.5 text-left transition-colors",
                    isSelected
                      ? "border-accent/30 bg-accent/10 dark:border-white/40 dark:bg-white/8"
                      : "border-accent/10 bg-surface hover:border-accent/25 hover:bg-accent/5 dark:border-white/10 dark:hover:bg-white/5",
                  ].join(" ")}
                >
                  <MemberAvatar member={person} color="success" />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-bold capitalize">{person.fullname}</span>
                    {person.email_address && (
                      <span className="truncate text-xs text-muted">{person.email_address}</span>
                    )}
                  </span>
                  <span
                    className={[
                      "ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors",
                      isSelected
                        ? "border-accent bg-accent text-white dark:border-white dark:bg-white dark:text-black"
                        : "border-separator",
                    ].join(" ")}
                  >
                    {isSelected && <Check size={12} strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Selected panel ───────────────────────────────────────────────── */}
      {/* <aside className="flex flex-col gap-3 rounded-2xl border border-accent/10 bg-surface-secondary p-4 lg:sticky lg:top-0 lg:self-start dark:border-[#FFFFFF26] dark:bg-[#FFFFFF0F]">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold">
            Selected
            <span className="ml-1.5 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent dark:bg-white/15 dark:text-white">
              {participants.length}
            </span>
          </span>
          {participants.length > 0 && (
            <button
              type="button"
              onClick={() => updateFormData({ participants: [] })}
              className="text-xs font-medium text-muted transition-colors hover:text-danger"
            >
              Clear all
            </button>
          )}
        </div>

        {participants.length === 0 ? (
          <p className="py-4 text-xs leading-relaxed text-muted">
            Nobody added yet. Select people on the left and they'll show up here.
          </p>
        ) : (
          <ul className="flex max-h-[320px] gap-1.5 overflow-y-auto">
            {participants.map((member) => (
              <li key={member.id} className="flex items-center gap-2 rounded-xl bg-surface px-2 py-1.5 dark:bg-white/5">
                <MemberAvatar member={member} color="accent" />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold capitalize">{member.fullname}</span>
                <button
                  type="button"
                  onClick={() => toggleMember(member)}
                  aria-label={`Remove ${member.fullname}`}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                >
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside> */}

      <DesktopCreateMemberDrawer show={showCreate} onResp={onCreateMemberDrawerResp} />
    </div>
  );
};

export default ParticipantsStep;
